import React, { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import {
  ISessionSummarizeSettings,
  SessionSummaryModelEnum,
  SessionSummaryTypeEnum,
  summarizeSession,
} from '../../api/SummaryService';
import useLocalStorage from '../../hooks/useLocalStorage';
import useLocalStorageSave from '../../hooks/useLocalStorageSave';
import { useSnackbar } from 'notistack';
import { convertMarkdownToDocx, downloadDocx } from '../../libraries/md-to-docx';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../redux/store/IStore';
import { setSummarizingInProgress } from '../../redux/actions/actions';
import { config } from '../../config';

interface SummaryModalContentProps {
  sessionId: number;
  onClose: () => void;
}

// Map the enum values to their labels
const summaryTypeLabels = {
  [SessionSummaryTypeEnum.SHORT]: 'Krajši',
  [SessionSummaryTypeEnum.LONG]: 'Daljši',
};

// Map the enum values to their simplified labels
const summaryModelLabels: { [key in SessionSummaryModelEnum]: string } = {
  [SessionSummaryModelEnum.GPT_4]: 'stable',
  [SessionSummaryModelEnum.GPT_4_1]: 'next',
  [SessionSummaryModelEnum.GPT_4_1_NANO]: 'next-nano',
  [SessionSummaryModelEnum.GPT_4_1_MINI]: 'next-mini',
  [SessionSummaryModelEnum.GPT_3_5_TURBO]: 'turbo',
};

// Helper function to get the default model from environment variable
const getDefaultSummaryModel = (): SessionSummaryModelEnum => {
  const envModel = config.defaultSummaryModel;

  // Map environment variable values to enum values
  switch (envModel) {
    case 'stable':
      return SessionSummaryModelEnum.GPT_4;
    case 'next':
      return SessionSummaryModelEnum.GPT_4_1;
    case 'next-nano':
      return SessionSummaryModelEnum.GPT_4_1_NANO;
    case 'next-mini':
      return SessionSummaryModelEnum.GPT_4_1_MINI;
    case 'turbo':
      return SessionSummaryModelEnum.GPT_3_5_TURBO;
    default:
      // If the environment variable doesn't match any known value, default to 'next'
      return SessionSummaryModelEnum.GPT_4_1;
  }
};

const SummaryModalContent: React.FC<SummaryModalContentProps> = ({ sessionId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const defaultSettings: ISessionSummarizeSettings = {
    summaryType: SessionSummaryTypeEnum.SHORT,
    model: getDefaultSummaryModel(),
    enableKeywords: false,
    enableKeyPoints: false,
    enableSpeakers: false,
    enableAgendaItems: false,
  };

  const sessionName = useSelector((state: IStore) => state.sessionName);
  const summaryExists = useSelector((state: IStore) => state.summaryExists);
  const summarizingInProgress = useSelector((state: IStore) => state.summarizingInProgress);
  const summarizingAllowed = useSelector((state: IStore) => state.summarizingAllowed);
  const summaryContent = useSelector((state: IStore) => state.summaryContent);

  const [summarySettings, setSummarySettings] = useState<ISessionSummarizeSettings>(defaultSettings);

  const { getSummarySettings } = useLocalStorage();
  const [saveToLocalStorage] = useLocalStorageSave('summarize-settings');

  // Load settings from local storage
  useEffect(() => {
    const settings = getSummarySettings();
    if (settings) {
      setSummarySettings((prev) => ({
        ...prev,
        ...settings, // Merge stored settings with defaults
      }));
    }
  }, [getSummarySettings]);

  // Save settings to local storage whenever they change
  useEffect(() => {
    saveToLocalStorage(summarySettings);
  }, [summarySettings, saveToLocalStorage]);

  const handleSummarySettingChange = (key: keyof ISessionSummarizeSettings, value: any) => {
    setSummarySettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  function removeTrailingHashes(input: string) {
    return input
      .split('\n') // Split into lines to process each line separately
      .map((line) =>
        line.replace(/^(#+)(.*?)\s*#+\s*$/, (_, hashesBefore, text) => {
          // Correctly return only the leading hashes and text
          return `${hashesBefore} ${text.trim()}`;
        })
      )
      .join('\n'); // Join lines back together
  }

  const handleExportToDoc = () => {
    const summaryContentToExport: string = removeTrailingHashes(summaryContent);
    convertMarkdownToDocx(summaryContentToExport)
      .then((blob) => {
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const summaryOutputFilename = sessionName + ' (povzetek) - ' + currentDate + '.docx';
        downloadDocx(blob, summaryOutputFilename);
      })
      .catch((error) => {
        enqueueSnackbar('Napaka pri izvozu povzetka. Prosimo poizkusite znova.', { variant: 'error' });
      });
  };

  const handleSummarizeButtonClick = () => {
    summarizeSession(sessionId, summarySettings)
      .then((response) => {
        dispatch(setSummarizingInProgress(true));
      })
      .catch((error) => {
        dispatch(setSummarizingInProgress(false));
      });
  };

  return (
    <div className="summary_modal_wrapper">
      <div className="summary_modal_content">
        <div className="summary_modal_title">Povzetek</div>
        <div className="summary_modal_settings">
          {/* Model selection is hidden */}

          <div className="summary_modal_section">
            <div className="summary_modal_section_title">Dolžina povzetka:</div>
            <div className="summary_modal_radio_buttons">
              {Object.values(SessionSummaryTypeEnum).map((summary_type) => (
                <label className="summary_modal_radio_label" key={summary_type}>
                  <input
                    type="radio"
                    name="summary_type"
                    value={summary_type}
                    checked={summarySettings.summaryType === summary_type}
                    onChange={() => handleSummarySettingChange('summaryType', summary_type)}
                  />
                  {summaryTypeLabels[summary_type]}
                </label>
              ))}
            </div>
          </div>

          <div className="summary_modal_section">
            <div className="summary_modal_section_title">Nastavitve:</div>
            <div className="summary_modal_checkboxes">
              <label className="summary_modal_checkbox_label">
                <input
                  type="checkbox"
                  checked={summarySettings.enableKeywords}
                  onChange={() =>
                    handleSummarySettingChange('enableKeywords', !summarySettings.enableKeywords)
                  }
                />
                Ključne besede
              </label>
              <label className="summary_modal_checkbox_label">
                <input
                  type="checkbox"
                  checked={summarySettings.enableKeyPoints}
                  onChange={() =>
                    handleSummarySettingChange('enableKeyPoints', !summarySettings.enableKeyPoints)
                  }
                />
                Ključne točke
              </label>
              <label className="summary_modal_checkbox_label">
                <input
                  type="checkbox"
                  checked={summarySettings.enableSpeakers}
                  onChange={() =>
                    handleSummarySettingChange('enableSpeakers', !summarySettings.enableSpeakers)
                  }
                />
                Vključi govorce
              </label>
              <label className="summary_modal_checkbox_label">
                <input
                  type="checkbox"
                  checked={summarySettings.enableAgendaItems}
                  onChange={() =>
                    handleSummarySettingChange('enableAgendaItems', !summarySettings.enableAgendaItems)
                  }
                />
                Vključi točke dnevnega reda
              </label>
            </div>
          </div>
        </div>
        <div>
          <div className="summary_modal_section summary_modal_buttons">
            {summaryExists && (
              <button className="button_primary summary_modal_button" onClick={handleExportToDoc}>
                Izvozi
              </button>
            )}
            <button
              className={`button_primary summary_modal_button ${
                summarizingInProgress || !summarizingAllowed ? 'summary_button_disabled' : ''
              }`}
              disabled={summarizingInProgress || !summarizingAllowed}
              onClick={handleSummarizeButtonClick}
            >
              {summarizingInProgress ? (
                <>
                  <span className="summary_spinner" />
                  Povzemam ...
                </>
              ) : (
                'Povzemi'
              )}
            </button>
          </div>
        </div>
        <div className="summary_content">{summaryExists && <Markdown>{summaryContent}</Markdown>}</div>
        <div className="summary_modal_section summary_modal_close_button">
          <button type="button" className="modal_button_disaggre" onClick={onClose}>
            Zapri
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryModalContent;
